import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../lib/firebase.ts';
import { GameRoom as GameRoomType, Player, Guess } from '../types/game.ts';
import { validateGuess, generateRoomCode, isWordValid } from '../utils/wordle.ts';
import { GameService } from '../services/gameService.ts';
import WordleGame from './WordleGame.tsx';
import PlayerList from './PlayerList.tsx';

const GameRoom: React.FC = () => {
    const { roomId } = useParams<{ roomId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [room, setRoom] = useState<GameRoomType | null>(null);
    const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null);
    const [currentGuess, setCurrentGuess] = useState('');
    const [hostWord, setHostWord] = useState('');
    const [hostWordValid, setHostWordValid] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isHost, setIsHost] = useState(false);

    // Firebase ile oda yönetimi
    useEffect(() => {
        const initializeRoom = async () => {
            try {
                setIsLoading(true);
                const playerName = searchParams.get('player') || searchParams.get('host') || 'Oyuncu';
                const hostStatus = !!searchParams.get('host');
                setIsHost(hostStatus);

                // Session persistence için localStorage kontrolü
                const sessionKey = `wordle_session_${roomId}`;
                const existingSession = localStorage.getItem(sessionKey);

                let newPlayer: Player;

                if (existingSession && hostStatus) {
                    // Host için: mevcut session'ı kullan
                    const sessionData = JSON.parse(existingSession);
                    newPlayer = sessionData.player;
                    const existingRoom = await GameService.getRoom(roomId!);
                    if (existingRoom && existingRoom.hostId === newPlayer.id) {
                        // Host gerçekten bu odanın host'u
                        setRoom(existingRoom);
                        setCurrentPlayer(newPlayer);
                        setIsLoading(false);
                        return;
                    } else {
                        // Host değil veya oda bulunamadı, yeni host olarak oda oluştur
                        console.log('Host session bulunamadı, yeni oda oluşturuluyor');
                    }
                } else if (existingSession && !hostStatus) {
                    // Oyuncu için: oda var mı ve oyuncu odada mı kontrol et
                    const existingRoom = await GameService.getRoom(roomId!);
                    if (existingRoom) {
                        const sessionData = JSON.parse(existingSession);
                        newPlayer = sessionData.player;

                        // Oyuncu hala odada mı kontrol et
                        const playerInRoom = existingRoom.players?.find(p => p.id === newPlayer.id);
                        if (playerInRoom) {
                            // Oyuncu odada, devam et
                            setRoom(existingRoom);
                            setCurrentPlayer(newPlayer);
                            setIsLoading(false);
                            return;
                        } else {
                            // Oyuncu odada değil, isim girişi sayfasına yönlendir
                            navigate(`/join?room=${roomId}`);
                            return;
                        }
                    } else {
                        // Oda yok, isim girişi sayfasına yönlendir
                        navigate(`/join?room=${roomId}`);
                        return;
                    }
                }

                // Yeni session oluştur
                newPlayer = {
                    id: Math.random().toString(36).substring(2, 15),
                    name: playerName,
                    guesses: [],
                    isGameComplete: false,
                    hasWon: false,
                    isHost: hostStatus
                };

                if (hostStatus) {
                    // Oda oluştur
                    const roomCode = roomId || generateRoomCode();
                    const newRoom = await GameService.createRoom(roomCode, newPlayer);
                    setRoom(newRoom);

                    // Host session'ını kaydet
                    localStorage.setItem(sessionKey, JSON.stringify({
                        player: newPlayer,
                        roomCode: roomCode
                    }));
                } else {
                    // Odaya katıl
                    const existingRoom = await GameService.joinRoom(roomId!, newPlayer);
                    setRoom(existingRoom);

                    // Oyuncu session'ını kaydet
                    localStorage.setItem(sessionKey, JSON.stringify({
                        player: newPlayer,
                        roomCode: roomId
                    }));
                }

                setCurrentPlayer(newPlayer);
                setIsLoading(false);
            } catch (error) {
                console.error('Oda başlatma hatası:', error);
                setError('Oda oluşturulurken bir hata oluştu');
                setIsLoading(false);
            }
        };

        initializeRoom();
    }, [roomId, searchParams, navigate]);

    // Sayfa kapatıldığında host ise odayı sil
    useEffect(() => {
        const handleBeforeUnload = async () => {
            if (isHost && room) {
                try {
                    await GameService.notifyRoomDeleted(room.code);
                    await GameService.deleteRoom(room.code);
                } catch (error) {
                    console.error('Oda silme hatası:', error);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isHost, room]);

    // Oyuncular için oda silindi bildirimini dinle
    useEffect(() => {
        if (!roomId || isHost) return;

        const notificationRef = ref(database, `notifications/${roomId}`);
        const unsubscribe = onValue(notificationRef, (snapshot) => {
            if (snapshot.exists()) {
                const notification = snapshot.val();
                if (notification.type === 'room_deleted') {
                    alert(notification.message);
                    // Session'ı temizle ve ana ekrana yönlendir
                    const sessionKey = `wordle_session_${roomId}`;
                    localStorage.removeItem(sessionKey);
                    navigate('/');
                }
            }
        });

        return () => {
            unsubscribe();
        };
    }, [roomId, isHost, navigate]);

    // Real-time oda güncellemelerini dinle
    useEffect(() => {
        if (!roomId) return;

        const subscription = GameService.subscribeToRoom(roomId, (updatedRoom: GameRoomType) => {
            console.log('Room updated:', updatedRoom); // Debug için
            setRoom(updatedRoom);

            // Mevcut oyuncuyu güncelle
            const updatedPlayer = updatedRoom.players?.find(p => p.id === currentPlayer?.id);
            if (updatedPlayer) {
                setCurrentPlayer(updatedPlayer);
            }
        });

        return () => {
            GameService.unsubscribe(subscription);
        };
    }, [roomId, currentPlayer?.id]);

    // Host word validation
    useEffect(() => {
        const validateHostWord = async () => {
            if (hostWord.length === 5) {
                const isValid = await isWordValid(hostWord);
                setHostWordValid(isValid);
            } else {
                setHostWordValid(null);
            }
        };

        validateHostWord();
    }, [hostWord]);

    const handleGuessChange = (guess: string) => {
        setCurrentGuess(guess);
    };

    const handleGuessSubmit = async () => {
        if (!room || !currentPlayer || currentGuess.length !== 5) return;

        try {
            const tiles = validateGuess(currentGuess, room.targetWord);
            const newGuess: Guess = {
                tiles,
                isComplete: true
            };

            const hasWon = tiles.every(tile => tile.state === 'correct');
            const isGameComplete = hasWon || (currentPlayer.guesses?.length || 0) + 1 >= room.maxGuesses;

            const updatedGuesses = [...(currentPlayer.guesses || []), newGuess];

            // Firebase'e tahmini ve oyuncu durumunu kaydet
            await GameService.updatePlayerGuess(room.code, currentPlayer.id, updatedGuesses);
            await GameService.updatePlayer(room.code, currentPlayer.id, {
                isGameComplete,
                hasWon
            });

            // Local state'i güncelle
            const updatedPlayer: Player = {
                ...currentPlayer,
                guesses: updatedGuesses,
                isGameComplete,
                hasWon
            };

            setCurrentPlayer(updatedPlayer);
            setCurrentGuess('');

            // Eğer oyun bitti ise oda durumunu güncelle
            if (isGameComplete) {
                const allPlayersComplete = room.players?.every(p =>
                    p.id === currentPlayer.id ? isGameComplete : p.isGameComplete
                ) || false;
                if (allPlayersComplete) {
                    await GameService.updateRoom(room.code, { gameStatus: 'finished' });
                }
            }
        } catch (error) {
            console.error('Tahmin kaydetme hatası:', error);
            setError('Tahmin kaydedilirken bir hata oluştu');
        }
    };

    const copyRoomCode = () => {
        if (room) {
            navigator.clipboard.writeText(room.code);
            alert(`Oda kodu kopyalandı: ${room.code}`);
        }
    };

    const copyShareLink = () => {
        if (room) {
            const shareLink = `https://grup-wordle-v2.vercel.app/join?room=${room.code}`;
            navigator.clipboard.writeText(shareLink);
            alert('Paylaşım linki kopyalandı!');
        }
    };


    const submitWord = async () => {
        if (room && hostWord.length === 5 && hostWordValid) {
            try {
                await GameService.submitWord(room.code, hostWord);
                setHostWord('');
                setHostWordValid(null);
            } catch (error) {
                console.error('Kelime gönderme hatası:', error);
                setError('Kelime gönderilirken bir hata oluştu');
            }
        }
    };

    const restartGame = async () => {
        if (!room || !isHost) return;

        try {
            // Oyunu sıfırla - oyuncuları odada tut
            await GameService.updateRoom(room.code, {
                gameStatus: 'waiting',
                targetWord: '',
                wordSubmitted: false,
                updatedAt: new Date().toISOString()
            });

            // Tüm oyuncuların oyun durumunu sıfırla
            if (room.players) {
                for (const player of room.players) {
                    await GameService.updatePlayer(room.code, player.id, {
                        guesses: [],
                        isGameComplete: false,
                        hasWon: false
                    });
                }
            }

            setHostWord('');
            setHostWordValid(null);
        } catch (error) {
            console.error('Oyun yeniden başlatma hatası:', error);
            setError('Oyun yeniden başlatılırken bir hata oluştu');
        }
    };


    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-white text-xl">Yükleniyor...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-400 text-xl">{error}</div>
            </div>
        );
    }

    if (!room || !currentPlayer) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-white text-xl">Oda yükleniyor...</div>
            </div>
        );
    }

    const canSubmitWord = isHost && room.gameStatus === 'waiting' && !room.wordSubmitted;
    const isGameActive = room.gameStatus === 'playing' && room.targetWord;

    return (
        <div className="min-h-screen bg-wordle-dark p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                    <div className="w-full sm:w-auto">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white">Grup Wordle</h1>
                        <p className="text-gray-300 text-sm sm:text-base">Oda Kodu: <span className="font-mono text-wordle-green">{room.code}</span></p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        {isHost && (
                            <button
                                onClick={restartGame}
                                className="btn out text-xs sm:text-sm"
                            >
                                Yeniden Başlat
                            </button>
                        )}
                        <button
                            onClick={copyRoomCode}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Kodu Kopyala
                        </button>
                        <button
                            onClick={copyShareLink}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Link Paylaş
                        </button>
                        <button
                            onClick={async () => {
                                // Host ise odayı sil ve oyunculara bildirim gönder
                                if (isHost && room) {
                                    try {
                                        await GameService.notifyRoomDeleted(room.code);
                                        await GameService.deleteRoom(room.code);
                                    } catch (error) {
                                        console.error('Oda silme hatası:', error);
                                    }
                                }

                                // Session'ı temizle
                                const sessionKey = `wordle_session_${roomId}`;
                                localStorage.removeItem(sessionKey);
                                navigate('/');
                            }}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Çıkış
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Oyun alanı */}
                    <div className="lg:col-span-2">
                        {room.gameStatus === 'waiting' && (
                            <div className="text-center py-6 sm:py-12">
                                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">
                                    {isHost ? 'Kelimeyi belirleyin' : 'Host kelimeyi belirliyor...'}
                                </h2>
                                <p className="text-gray-300 mb-6">
                                    Oda kodunu paylaşın: <span className="font-mono text-wordle-green text-xl">{room.code}</span>
                                </p>

                                {canSubmitWord && (
                                    <div className="max-w-md mx-auto">
                                        <div className="mb-4">
                                            <label className="block text-white text-sm font-medium mb-2">
                                                5 harfli kelime girin:
                                            </label>
                                            <input
                                                type="text"
                                                value={hostWord}
                                                onChange={(e) => setHostWord(e.target.value.toUpperCase())}
                                                placeholder="Kelime girin"
                                                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-wordle-green focus:outline-none text-center text-xl font-mono"
                                                maxLength={5}
                                            />
                                            {hostWord.length === 5 && hostWordValid === false && (
                                                <p className="text-red-400 text-sm mt-2">
                                                    Geçerli bir kelime girin
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={submitWord}
                                            disabled={hostWord.length !== 5 || !hostWordValid}
                                            className="px-6 py-3 bg-wordle-green hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                                        >
                                            Kelimeyi Gönder
                                        </button>
                                    </div>
                                )}

                                {!isHost && room.gameStatus === 'waiting' && (
                                    <p className="text-gray-400">
                                        Host kelimeyi belirleyene kadar bekleyin...
                                    </p>
                                )}
                            </div>
                        )}

                        {isGameActive && (
                            <div>
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-semibold text-white mb-2">
                                        {isHost ? 'Diğer oyuncular kelimeyi bulmaya çalışıyor...' : 'Kelimeyi bulun!'}
                                    </h2>
                                    <p className="text-gray-300">
                                        {(currentPlayer.guesses?.length || 0)}/{room.maxGuesses} tahmin
                                    </p>
                                </div>

                                {!isHost ? (
                                    <WordleGame
                                        targetWord={room.targetWord}
                                        currentGuess={currentGuess}
                                        onGuessChange={handleGuessChange}
                                        onGuessSubmit={handleGuessSubmit}
                                        guesses={currentPlayer.guesses || []}
                                        maxGuesses={room.maxGuesses}
                                        isGameComplete={currentPlayer.isGameComplete || false}
                                        hasWon={currentPlayer.hasWon || false}
                                        disabled={!isGameActive}
                                    />
                                ) : (
                                    <div className="text-center py-6 sm:py-12">
                                        <div className="bg-gray-800 rounded-lg p-4 sm:p-8">
                                            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">
                                                Belirlediğiniz Kelime:
                                            </h3>
                                            <div className="bg-wordle-green text-white font-mono text-2xl sm:text-3xl py-3 sm:py-4 px-4 sm:px-6 rounded-lg mb-4">
                                                {room.targetWord}
                                            </div>
                                            <p className="text-gray-300 text-sm sm:text-base">
                                                Diğer oyuncular bu kelimeyi bulmaya çalışıyor.
                                                Onların tahminlerini aşağıda görebilirsiniz.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {room.gameStatus === 'finished' && (
                            <div className="text-center py-6 sm:py-12">
                                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">Oyun Bitti!</h2>
                                <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Kelime: <span className="text-wordle-green font-mono">{room.targetWord}</span></h3>
                                    <div className="space-y-2">
                                        {room.players?.sort((a, b) => {
                                            // Host her zaman en üstte
                                            if (a.isHost) return -1;
                                            if (b.isHost) return 1;

                                            // Kazananlar önce
                                            if (a.hasWon && !b.hasWon) return -1;
                                            if (!a.hasWon && b.hasWon) return 1;

                                            // İkisi de kazandıysa tahmin sayısına göre sırala
                                            if (a.hasWon && b.hasWon) {
                                                return (a.guesses?.length || 0) - (b.guesses?.length || 0);
                                            }

                                            // İkisi de kazanmadıysa tahmin sayısına göre sırala
                                            if (!a.hasWon && !b.hasWon) {
                                                return (a.guesses?.length || 0) - (b.guesses?.length || 0);
                                            }

                                            return 0;
                                        }).map(player => (
                                            <div key={player.id} className={`flex justify-between items-center p-2 sm:p-3 rounded-lg ${player.hasWon ? 'bg-wordle-green bg-opacity-20' : 'bg-gray-700'}`}>
                                                <span className="text-white font-medium text-sm sm:text-base">{player.name}</span>
                                                <span className={`font-semibold text-xs sm:text-sm ${player.hasWon ? 'text-wordle-green' : 'text-red-400'}`}>
                                                    {player.hasWon ? `🏆 ${player.guesses?.length || 0} tahminle kazandı` : '❌ Başarısız'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {isHost ? (
                                    <div className="space-y-4">
                                        <p className="text-gray-300 mb-4">Yeni oyun başlatmak için aşağıdaki butona tıklayın</p>
                                        <button
                                            onClick={restartGame}
                                            className="btn"
                                        >
                                            Yeni Oyun Başlat
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <p className="text-gray-300 mb-4">Host yeni oyun başlatana kadar bekleyin...</p>
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-wordle-green"></div>
                                            <span className="text-gray-300">Bekleniyor...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Oyuncu listesi */}
                    <div className="max-h-64 sm:max-h-none overflow-y-auto">
                        <PlayerList
                            players={room.players || []}
                            currentPlayerId={currentPlayer.id}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameRoom;
